import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft,
  Check,
  ChevronsUpDown,
  AlertTriangle,
  MapPin,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Checkbox } from '@/app/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/app/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/app/components/ui/popover';
import { Alert, AlertDescription, AlertTitle } from '@/app/components/ui/alert';
import { Badge } from '@/app/components/ui/badge';
import { toast } from '@/app/components/ui/simple-toaster';
import { cn } from '@/app/components/ui/utils';
import { useAsset, useAssets, useCheckInAsset } from '@/hooks/use-assets';
import { useLocations } from '@/hooks/use-deployments';
import type { Asset, CheckInPayload } from '@/lib/types';

const checkInSchema = z.object({
  asset: z.number({ error: 'Please select an asset' }).min(1, 'Please select an asset'),
  location: z.number().optional(),
  notes: z.string().optional(),
  flag_for_inspection: z.boolean().optional(),
});

type CheckInFormData = z.infer<typeof checkInSchema>;

const OUT_OF_SERVICE_RETURN_MESSAGE = 'Returned — still out of service, needs inspection';

function outOfServiceLabel(asset: Asset) {
  return asset.status === 'damaged' ? 'Damaged' : 'Quarantined';
}

export function CheckInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedAssetIdParam = searchParams.get('asset');
  const preselectedAssetId = preselectedAssetIdParam ? parseInt(preselectedAssetIdParam, 10) : 0;
  const [assetSearch, setAssetSearch] = useState('');
  const [assetOpen, setAssetOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [flagForInspection, setFlagForInspection] = useState(false);

  const { data: assetsData, isLoading: assetsLoading } = useAssets({
    page_size: 50,
    check_in_eligible: true,
    q: assetSearch,
  });
  const { data: locations } = useLocations();
  const checkInMutation = useCheckInAsset();

  const deployedAssets = assetsData?.results || [];
  const stillOutOfService = Boolean(selectedAsset?.out_of_service_checkout_open);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CheckInFormData>({
    resolver: zodResolver(checkInSchema),
  });

  // Opened from an asset page ("Check In" button): pre-select that asset.
  const { data: preselectedAsset } = useAsset(preselectedAssetId);

  useEffect(() => {
    if (preselectedAsset && !selectedAsset) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedAsset(preselectedAsset);
      setValue('asset', preselectedAsset.id);
    }
  }, [preselectedAsset, selectedAsset, setValue]);

  const onSubmit = async (data: CheckInFormData) => {
    try {
      const payload: CheckInPayload = {
        location: data.location,
        notes: data.notes,
      };

      if (flagForInspection && data.notes) {
        payload.notes = `[FLAGGED FOR INSPECTION] ${data.notes}`;
      } else if (flagForInspection) {
        payload.notes = '[FLAGGED FOR INSPECTION]';
      }

      await checkInMutation.mutateAsync({ id: data.asset, data: payload });
      toast.success(
        stillOutOfService
          ? OUT_OF_SERVICE_RETURN_MESSAGE
          : 'Asset checked in successfully'
      );
      navigate('/deployments');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to check in asset';
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as { response?: { data?: { detail?: string; error?: string } } };
        const detail = axiosError.response?.data?.detail || axiosError.response?.data?.error;
        if (detail) {
          toast.error(detail);
          return;
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleAssetSelect = (asset: Asset) => {
    setSelectedAsset(asset);
    setValue('asset', asset.id);
    setAssetOpen(false);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/deployments')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Check In Asset</h1>
          <p className="text-gray-600 mt-1">Return equipment from deployment</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Asset Selection</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className="space-y-2"
              data-field-container
              data-required="true"
              data-required-empty={selectedAsset ? 'false' : 'true'}
              data-required-message="Please select an asset."
              tabIndex={-1}
            >
              <Label>Select Deployed Asset *</Label>
              <Popover open={assetOpen} onOpenChange={setAssetOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={assetOpen}
                    className={cn(
                      'w-full justify-between',
                      !selectedAsset && 'text-muted-foreground'
                    )}
                  >
                    {selectedAsset ? (
                      <span>
                        {selectedAsset.display_name} ({selectedAsset.serial_number})
                      </span>
                    ) : (
                      'Search for a deployed asset...'
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search by name or serial number..."
                      value={assetSearch}
                      onValueChange={setAssetSearch}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {assetsLoading ? 'Loading...' : 'No deployed assets found.'}
                      </CommandEmpty>
                      <CommandGroup>
                        {deployedAssets.map((asset) => (
                          <CommandItem
                            key={asset.id}
                            value={`${asset.display_name} ${asset.serial_number}`}
                            onSelect={() => handleAssetSelect(asset)}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedAsset?.id === asset.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <div className="flex flex-col">
                              <span className="font-medium">{asset.display_name}</span>
                              <span className="text-sm text-gray-500">
                                {asset.serial_number}
                                {asset.manufacturer && ` - ${asset.manufacturer}`}
                              </span>
                              {asset.out_of_service_checkout_open && (
                                <span className="text-xs font-medium text-red-700">
                                  {outOfServiceLabel(asset)} — needs inspection
                                </span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.asset && (
                <p className="text-sm text-red-500">{errors.asset.message}</p>
              )}
            </div>

            {selectedAsset && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-medium text-gray-900">
                    {stillOutOfService ? 'Out-of-Service Asset' : 'Currently Deployed Asset'}
                  </h4>
                  {stillOutOfService ? (
                    <Badge variant="secondary" className="bg-red-100 text-red-800">
                      {outOfServiceLabel(selectedAsset)}
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                      Deployed
                    </Badge>
                  )}
                </div>
                {stillOutOfService && (
                  <Alert variant="destructive" data-testid="out-of-service-return">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{OUT_OF_SERVICE_RETURN_MESSAGE}</AlertTitle>
                    <AlertDescription>
                      Checking in closes the checkout and takes it off the job. The status
                      stays {outOfServiceLabel(selectedAsset)}; only an inspector can return
                      it to service.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>{' '}
                    <span className="font-medium">{selectedAsset.display_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Serial:</span>{' '}
                    <span className="font-medium">{selectedAsset.serial_number}</span>
                  </div>
                  {selectedAsset.manufacturer && (
                    <div>
                      <span className="text-gray-500">Manufacturer:</span>{' '}
                      <span className="font-medium">{selectedAsset.manufacturer}</span>
                    </div>
                  )}
                  {selectedAsset.length_m && (
                    <div>
                      <span className="text-gray-500">Length:</span>{' '}
                      <span className="font-medium">{selectedAsset.length_m}m</span>
                    </div>
                  )}
                </div>

                {selectedAsset.is_overdue && (
                  <Alert variant="destructive" className="mt-3">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Inspection Overdue</AlertTitle>
                    <AlertDescription>
                      This asset has an overdue inspection. Consider scheduling an inspection after check-in.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Return Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="location">Return Location</Label>
              <Select
                onValueChange={(value) =>
                  setValue('location', value ? parseInt(value, 10) : undefined)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select return location (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {locations?.map((location) => (
                    <SelectItem key={location.id} value={location.id.toString()}>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        {location.name}
                        <span className="text-gray-500 text-xs capitalize">
                          ({location.type})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500">
                Where is the asset being returned to?
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Condition Notes</Label>
              <Textarea
                id="notes"
                placeholder="Describe the condition of the asset upon return, any damage, wear, or issues observed..."
                rows={4}
                {...register('notes')}
              />
            </div>

            <div className="flex items-start space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <Checkbox
                id="flag_for_inspection"
                checked={flagForInspection}
                onCheckedChange={(checked) => setFlagForInspection(checked === true)}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="flag_for_inspection"
                  className="text-sm font-medium cursor-pointer"
                >
                  Flag for Inspection
                </Label>
                <p className="text-sm text-gray-600">
                  Check this box if the asset should be inspected before being deployed again.
                  This is recommended if you noticed any damage, wear, or concerns during use.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/deployments')}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Checking In...' : 'Check In Asset'}
          </Button>
        </div>
      </form>
    </div>
  );
}
